import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, sessionHeaders } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Brain, Compass, ArrowRight, ArrowLeft, ShieldCheck, Mail, Check,
  ChevronRight, ChevronLeft, Loader2, Lock, Timer,
} from "lucide-react";
import Report from "@/components/Report";
import { CANDIDATE, DOMAINS as D_IDS, RUNNER } from "@/constants/testIds";

export default function CandidateFlow() {
  const navigate = useNavigate();
  const [step, setStep] = useState("choice"); // choice|entry|verify|domains|test|report|msis-info
  const [testType, setTestType] = useState(null);
  const [testId, setTestId] = useState("");
  const [takerEmail, setTakerEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [code, setCode] = useState("");
  const [session, setSession] = useState(null);
  const [config, setConfig] = useState({ domainChoices: [2, 3, 4, 5, 6, 7, 8, 9], domains: [] });
  const [domainCount, setDomainCount] = useState(null);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [items, setItems] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [report, setReport] = useState(null);
  const [questionSeconds, setQuestionSeconds] = useState(30);
  const [timeLeft, setTimeLeft] = useState(30);
  const answersRef = useRef({});
  const firedRef = useRef(false);

  const reset = () => {
    setStep("choice"); setTestType(null); setTestId(""); setTakerEmail(""); setError(""); setMaskedEmail("");
    setCode(""); setSession(null); setDomainCount(null); setSelectedDomains([]);
    setItems([]); setAnswers({}); setIdx(0); setReport(null);
  };

  // ---------- Step: choice ----------
  const chooseTest = (t) => {
    setTestType(t);
    setError("");
    setStep("entry");
  };
  const perDomain = testType === "MSIS" ? 25 : 30;

  // ---------- Step: entry (validate) ----------
  const validate = async () => {
    if (!testId.trim()) { setError("Please enter your Test ID."); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(takerEmail.trim())) { setError("Please enter a valid email address to receive your code and report."); return; }
    setLoading(true); setError("");
    try {
      await api.post("/test/validate", { testId: testId.trim(), testType });
      const { data } = await api.post("/test/send-code", { testId: testId.trim(), email: takerEmail.trim() });
      setMaskedEmail(data.email);
      toast.success(`Verification code sent to ${data.email}`);
      setStep("verify");
    } catch (e) {
      setError(apiError(e));
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      await api.post("/test/send-code", { testId: testId.trim(), email: takerEmail.trim() });
      toast.success("A new code has been sent.");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  // ---------- Step: verify ----------
  const verify = async () => {
    if (code.trim().length < 6) { setError("Enter the 6-digit code."); return; }
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/test/verify-code", { testId: testId.trim(), code: code.trim(), testType });
      setSession(data.sessionToken);
      setConfig({ domainChoices: data.domainChoices, domains: data.domains });
      setStep("domains");
    } catch (e) {
      setError(apiError(e));
    } finally { setLoading(false); }
  };

  // ---------- Step: domains ----------
  const toggleDomain = (code) => {
    setSelectedDomains((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      if (prev.length >= domainCount) return prev; // limit
      return [...prev, code];
    });
  };

  const startTest = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/test/start",
        { testId: testId.trim(), domainCount, selectedDomains },
        { headers: sessionHeaders(session) });
      setItems(data.items);
      setAnswers({});
      answersRef.current = {};
      setQuestionSeconds(data.questionSeconds || 30);
      setIdx(0);
      setStep("test");
    } catch (e) { setError(apiError(e)); toast.error(apiError(e)); } finally { setLoading(false); }
  };

  // ---------- Step: test ----------
  const answer = (itemId, opt) => setAnswers((a) => { const next = { ...a, [itemId]: opt }; answersRef.current = next; return next; });
  const answeredCount = Object.keys(answers).length;
  const defaultAnswer = () => (testType === "MSIS" ? "3" : "C");

  const submit = async (finalAnswers) => {
    const base = finalAnswers || answersRef.current;
    const payload = { ...base };
    items.forEach((it) => { if (!payload[it.id]) payload[it.id] = defaultAnswer(); });
    setLoading(true);
    try {
      const { data } = await api.post("/test/submit",
        { testId: testId.trim(), answers: payload },
        { headers: sessionHeaders(session) });
      setReport(data);
      setStep("report");
      window.scrollTo(0, 0);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const handleTimeUp = () => {
    const curItem = items[idx];
    if (!curItem) return;
    const cur = { ...answersRef.current };
    if (!cur[curItem.id]) cur[curItem.id] = defaultAnswer();
    setAnswers(cur);
    answersRef.current = cur;
    if (idx >= items.length - 1) submit(cur);
    else setIdx((i) => i + 1);
  };

  const goNext = () => {
    const curItem = items[idx];
    if (curItem && !answersRef.current[curItem.id]) {
      const cur = { ...answersRef.current, [curItem.id]: defaultAnswer() };
      setAnswers(cur); answersRef.current = cur;
    }
    setIdx((i) => Math.min(items.length - 1, i + 1));
  };

  // Per-question countdown: resets each question. The timeout side-effect fires
  // from a separate effect exactly once (StrictMode-safe: no side effects in updater).
  useEffect(() => {
    if (step !== "test" || items.length === 0) return undefined;
    firedRef.current = false;
    setTimeLeft(questionSeconds);
    const t = setInterval(() => setTimeLeft((s) => (s <= 0 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [step, idx, questionSeconds, items.length]);

  useEffect(() => {
    if (step === "test" && timeLeft === 0 && !firedRef.current) {
      firedRef.current = true;
      handleTimeUp();
    }
    // eslint-disable-next-line
  }, [timeLeft, step]);

  // ============ RENDER ============
  if (step === "report" && report) {
    return <Report report={report} onRestart={reset} />;
  }

  if (step === "test") {
    const it = items[idx];
    const pct = Math.round(((idx + 1) / items.length) * 100);
    const isLast = idx === items.length - 1;
    const isMsis = testType === "MSIS";
    const likert = [
      { k: "1", label: "Strongly Disagree" },
      { k: "2", label: "Disagree" },
      { k: "3", label: "Neutral" },
      { k: "4", label: "Agree" },
      { k: "5", label: "Strongly Agree" },
    ];
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-5 py-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="eyebrow">{testType} &middot; Question {idx + 1} of {items.length}</span>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 hidden sm:inline">{answeredCount} answered</span>
                <span data-testid="question-timer" className={`inline-flex items-center gap-1 font-mono font-semibold px-2.5 py-0.5 rounded-full transition-colors ${timeLeft <= 5 ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-700"}`}>
                  <Timer className="w-3.5 h-3.5" /> {timeLeft}s
                </span>
              </div>
            </div>
            <Progress data-testid={RUNNER.progress} value={pct} className="h-2" />
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-5 py-10">
          <div className="mb-6">
            <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">{it.domain} · {it.construct}</span>
          </div>
          <h2 data-testid={RUNNER.question} className="text-xl sm:text-2xl font-semibold leading-relaxed mb-8">{isMsis ? it.text : it.stem}</h2>
          {isMsis ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500 mb-2">How much do you agree with this statement?</p>
              {likert.map(({ k, label }) => {
                const selected = answers[it.id] === k;
                return (
                  <button
                    key={k}
                    data-testid={`${RUNNER.option}-${k}`}
                    onClick={() => answer(it.id, k)}
                    className={`w-full text-left px-5 py-4 rounded-lg border transition-colors flex items-center gap-4 ${
                      selected ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-400"
                    }`}
                  >
                    <span className={`shrink-0 w-7 h-7 rounded-full grid place-items-center text-sm font-bold ${
                      selected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                    }`}>{k}</span>
                    <span className="text-slate-800">{label}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {["A", "B", "C", "D"].map((k) => {
                const selected = answers[it.id] === k;
                return (
                  <button
                    key={k}
                    data-testid={`${RUNNER.option}-${k}`}
                    onClick={() => answer(it.id, k)}
                    className={`w-full text-left px-5 py-4 rounded-lg border transition-colors flex items-start gap-4 ${
                      selected ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-400"
                    }`}
                  >
                    <span className={`shrink-0 w-7 h-7 rounded-full grid place-items-center text-sm font-bold ${
                      selected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                    }`}>{k}</span>
                    <span className="text-slate-800 pt-0.5">{it.options[k]}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center justify-between mt-10">
            <Button variant="outline" className="rounded-full" data-testid={RUNNER.prevButton}
              disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            {isLast ? (
              <Button className="rounded-full bg-blue-700 hover:bg-blue-800 text-white" data-testid={RUNNER.submitButton}
                onClick={() => submit()} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Submit & View Report
              </Button>
            ) : (
              <Button className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid={RUNNER.nextButton}
                onClick={goNext}>
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* top brand bar */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-slate-900 grid place-items-center text-white font-bold font-head">M</div>
            <span className="font-head font-bold text-lg tracking-tight">MSI Scale</span>
          </button>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/pricing")} className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Buy a Test ID</button>
            <button data-testid={CANDIDATE.adminLink} onClick={() => navigate("/admin/login")}
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 transition-colors">
              <Lock className="w-3.5 h-3.5" /> Admin
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-14">
        {/* Step: choice */}
        {step === "choice" && (
          <div>
          <div className="stagger">
            <p className="eyebrow">Assessment Portal</p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mt-2 max-w-3xl">Choose the assessment you have been assigned</h1>
            <p className="text-slate-500 mt-4 max-w-2xl text-lg">Your Test ID works for either assessment — pick the one you want to take. Each Test ID can be used only once, for whichever test you start first.</p>
            <p className="text-slate-500 mt-2">Don't have a Test ID yet? <button onClick={() => navigate("/pricing")} className="text-blue-700 font-medium underline">Buy a test</button>.</p>
          </div>
            <div className="grid md:grid-cols-2 gap-6 mt-10">
              <button data-testid={CANDIDATE.msisCard} onClick={() => chooseTest("MSIS")}
                className="text-left p-8 rounded-xl bg-white border border-slate-200 hover:border-slate-900 hover:-translate-y-1 transition-transform group">
                <div className="w-12 h-12 rounded-lg bg-slate-100 grid place-items-center mb-5 group-hover:bg-slate-900 group-hover:text-white transition-colors"><Brain className="w-6 h-6" /></div>
                <h2 className="text-2xl font-bold">MSIS</h2>
                <p className="eyebrow mt-1">Inclination Scale</p>
                <p className="text-slate-500 mt-4">Management Specialization Inclination Scale — a psychometric measure of your attitudes, personality and preferences across management domains.</p>
                <span className="inline-flex items-center gap-1 mt-6 font-semibold text-slate-900">Enter Test ID <ArrowRight className="w-4 h-4" /></span>
              </button>
              <button data-testid={CANDIDATE.msosCard} onClick={() => chooseTest("MSOS")}
                className="text-left p-8 rounded-xl bg-slate-900 text-white border border-slate-900 hover:-translate-y-1 transition-transform group relative overflow-hidden">
                <span className="absolute top-5 right-5 text-[10px] tracking-widest uppercase bg-blue-600 px-2 py-1 rounded-full">New</span>
                <div className="w-12 h-12 rounded-lg bg-white/10 grid place-items-center mb-5"><Compass className="w-6 h-6 text-blue-300" /></div>
                <h2 className="text-2xl font-bold">MSOS</h2>
                <p className="eyebrow mt-1 text-blue-300">Orientation Scale</p>
                <p className="text-slate-300 mt-4">Management Specialization Orientation Scale — assesses your general-intelligence reasoning and indicates the domain your cues point toward. Right/wrong scored.</p>
                <span className="inline-flex items-center gap-1 mt-6 font-semibold text-white">Enter Test ID <ArrowRight className="w-4 h-4" /></span>
              </button>
            </div>
          </div>
        )}

        {/* Step: entry */}
        {step === "entry" && (
          <div className="max-w-md mx-auto stagger">
            <BackBtn onClick={() => { setStep("choice"); setError(""); }} />
            <p className="eyebrow">{testType} Assessment</p>
            <h1 className="text-3xl font-bold tracking-tight mt-2">Enter your Test ID</h1>
            <p className="text-slate-500 mt-3">Enter your Test ID and your own email address. We'll send a verification code and your report to this email — it does not need to match the email used to purchase the Test ID.</p>
            <div className="mt-8 space-y-4">
              <Input data-testid={CANDIDATE.testIdInput} placeholder={`${testType}-XXXXXX`} value={testId}
                onChange={(e) => setTestId(e.target.value.toUpperCase())}
                className="h-12 text-lg tracking-wide" />
              <Input data-testid="candidate-email-input" type="email" placeholder="you@example.com" value={takerEmail}
                onChange={(e) => setTakerEmail(e.target.value)}
                className="h-12 text-base" />
              {error && <p data-testid={CANDIDATE.errorMessage} className="text-sm text-red-600">{error}</p>}
              <Button data-testid={CANDIDATE.validateButton} onClick={validate} disabled={loading}
                className="w-full h-12 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-base">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Send verification code
              </Button>
            </div>
          </div>
        )}

        {/* Step: verify */}
        {step === "verify" && (
          <div className="max-w-md mx-auto stagger">
            <BackBtn onClick={() => { setStep("entry"); setError(""); setCode(""); }} />
            <div className="w-12 h-12 rounded-full bg-blue-50 grid place-items-center mb-4"><ShieldCheck className="w-6 h-6 text-blue-700" /></div>
            <h1 className="text-3xl font-bold tracking-tight">Verify your email</h1>
            <p className="text-slate-500 mt-3">Enter the 6-digit code sent to <span className="font-semibold text-slate-800">{maskedEmail}</span>.</p>
            <div className="mt-8 space-y-4">
              <Input data-testid={CANDIDATE.codeInput} placeholder="000000" value={code} inputMode="numeric" maxLength={6}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="h-14 text-2xl tracking-[0.5em] text-center font-semibold" />
              {error && <p data-testid={CANDIDATE.errorMessage} className="text-sm text-red-600">{error}</p>}
              <Button data-testid={CANDIDATE.verifyButton} onClick={verify} disabled={loading}
                className="w-full h-12 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-base">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Verify & continue
              </Button>
              <button onClick={resendCode} className="text-sm text-slate-500 hover:text-slate-900 w-full text-center transition-colors">Resend code</button>
            </div>
          </div>
        )}

        {/* Step: domains */}
        {step === "domains" && (
          <div className="max-w-4xl mx-auto stagger">
            <p className="eyebrow">{testType} Setup</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Design the scope of your test</h1>
            <p className="text-slate-500 mt-3">First choose how many domains to be assessed on, then pick which ones. Each domain adds {perDomain} {testType === "MSIS" ? "statements" : "scenario questions"}.</p>

            <div className="mt-8">
              <p className="eyebrow mb-3">1. Number of domains</p>
              <div className="flex flex-wrap gap-3">
                {config.domainChoices.map((n) => (
                  <button key={n} data-testid={`${D_IDS.countOption}-${n}`}
                    onClick={() => { setDomainCount(n); setSelectedDomains([]); }}
                    className={`px-6 py-4 rounded-lg border font-semibold transition-colors ${
                      domainCount === n ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 bg-white hover:border-slate-400"
                    }`}>
                    {n} domains
                    <span className="block text-xs font-normal text-slate-400 mt-0.5">{n * perDomain} questions</span>
                  </button>
                ))}
              </div>
            </div>

            {domainCount && (
              <div className="mt-8">
                <p className="eyebrow mb-3">2. Select {domainCount} domain{domainCount > 1 ? "s" : ""} <span className="text-slate-400">({selectedDomains.length}/{domainCount})</span></p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {config.domains.map((d) => {
                    const selected = selectedDomains.includes(d.code);
                    const disabled = !selected && selectedDomains.length >= domainCount;
                    return (
                      <button key={d.code} data-testid={`${D_IDS.domainCard}-${d.code}`}
                        onClick={() => toggleDomain(d.code)} disabled={disabled}
                        className={`text-left px-4 py-4 rounded-lg border transition-colors ${
                          selected ? "border-blue-600 bg-blue-50" : disabled ? "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed" : "border-slate-200 bg-white hover:border-slate-400"
                        }`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-400">{d.code}</span>
                          {selected && <Check className="w-4 h-4 text-blue-600" />}
                        </div>
                        <div className="font-semibold text-slate-800 mt-1 leading-snug">{d.name}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {error && <p className="text-sm text-red-600 mt-4">{error}</p>}
            <Button data-testid={D_IDS.continueButton} onClick={startTest}
              disabled={!domainCount || selectedDomains.length !== domainCount || loading}
              className="mt-8 h-12 px-8 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-base">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Begin assessment <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function BackBtn({ onClick }) {
  return (
    <button data-testid={CANDIDATE.backButton} onClick={onClick}
      className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
  );
}
